/**
 * The layout pipeline — docs/08-frontend-and-ux.md §4.2.
 *
 * Pure arithmetic, no DOM: the same scene and the same collapsed set always
 * produce the same coordinates, which is what makes the result cacheable, safe
 * to run in a worker, and — the reason it matters here — testable. The exit
 * criterion "no layout overlap in a 50-person intermarried stress fixture" is a
 * statement about numbers, and it is checked as one.
 */

import { fuzzyDateSortKey, type PersonSummary, type TreeEdge } from '@famtree/shared';
import { assignGenerations } from './layer.js';
import { buildCoupleGroups, type CoupleGroup } from './couples.js';
import { orderLayers } from './order.js';
import { assignCoordinates } from './coords.js';
import {
  DEFAULT_LAYOUT,
  type ChildDrop,
  type LayoutOptions,
  type PlacedPerson,
  type SceneInput,
  type SiblingBracket,
  type TreeLayout,
  type UnionJunction,
} from './types.js';

export * from './types.js';
export { assignGenerations } from './layer.js';
export { buildCoupleGroups } from './couples.js';

export function layoutTree(scene: SceneInput, options: Partial<LayoutOptions> = {}): TreeLayout {
  const opts = { ...DEFAULT_LAYOUT, ...options };
  const persons = new Map(scene.persons.map((person) => [person.id, person]));
  const personIds = scene.persons.map((person) => person.id);

  /*
   * §4.3: a step parent row is never drawn. The api already leaves it out of the
   * scene, and the layout drops it again — this module decides what appears on
   * the chart, so the rule that a step edge does not appear belongs here too.
   */
  const drawable = scene.edges.filter(
    (edge) => !(edge.kind === 'parent_child' && edge.parent_basis === 'step'),
  );

  /* 1 — generations */
  const { generation, violations } = assignGenerations(personIds, drawable);

  /* 2 — couple groups and spouse rails */
  const groups = buildCoupleGroups(personIds, drawable, generation);

  /* 3 — horizontal order */
  const parentsOf = new Map<string, string[]>();
  for (const edge of drawable) {
    if (edge.kind !== 'parent_child') continue;
    if (!persons.has(edge.a) || !persons.has(edge.b)) continue;
    parentsOf.set(edge.b, [...(parentsOf.get(edge.b) ?? []), edge.a]);
  }
  const layers = orderLayers({ groups, persons, parentsOf, sweeps: opts.sweeps });

  /* 4 — coordinates */
  const positions = assignCoordinates({ layers, parentsOf, opts });

  const placed = new Map<string, PlacedPerson>();
  const bandHeight = opts.cardHeight + opts.bandGap;
  const bands: TreeLayout['bands'] = [];
  let width = 0;

  for (const layer of layers) {
    for (const group of layer.groups) {
      for (const member of group.members) {
        const person = persons.get(member);
        const x = positions.get(member);
        if (!person || x === undefined) continue;
        placed.set(member, {
          id: member,
          person,
          generation: layer.generation,
          x,
          y: layer.generation * bandHeight,
        });
        width = Math.max(width, x + opts.cardWidth);
      }
    }
    bands.push({
      generation: layer.generation,
      y: layer.generation * bandHeight,
      height: bandHeight,
    });
  }

  /* 5 — junctions, drops and brackets */
  const junctions = buildJunctions(drawable, placed, opts);
  const drops = buildDrops(drawable, placed, junctions, generation);
  const brackets: SiblingBracket[] = drawable
    .filter((edge) => edge.kind === 'sibling' && placed.has(edge.a) && placed.has(edge.b))
    .map((edge) => ({ id: edge.id, a: edge.a, b: edge.b }));

  const height = bands.length * bandHeight;
  return { persons: placed, junctions, drops, brackets, bands, width, height, violations };
}

/**
 * One junction per union assertion, sitting between its partners and just below
 * their card — which is what lets two of Hassan's marriages each carry their own
 * bar, their own date chip and their own children (§4.3).
 */
function buildJunctions(
  edges: TreeEdge[],
  placed: Map<string, PlacedPerson>,
  opts: LayoutOptions,
): UnionJunction[] {
  const junctions: UnionJunction[] = [];
  for (const edge of edges) {
    if (edge.kind !== 'union') continue;
    const a = placed.get(edge.a);
    const b = placed.get(edge.b);
    if (!a || !b) continue;
    junctions.push({
      id: edge.id,
      a: edge.a,
      b: edge.b,
      unionType: edge.union_type,
      endReason: edge.end_reason,
      started: edge.started,
      ended: edge.ended,
      children: [],
      x: (a.x + b.x) / 2 + opts.cardWidth / 2,
      y: Math.max(a.y, b.y) + opts.cardHeight + opts.bandGap / 3,
    });
  }
  return junctions;
}

/**
 * Child drops, attached to a junction where the recorded parents share a union.
 *
 * Where they do not, the child gets a line from each parent card directly —
 * §4.1's "visibly two independent lines, an honest rendering of the data".
 * Inventing a junction for co-parents who were never recorded as a couple would
 * draw a marriage nobody asserted.
 */
function buildDrops(
  edges: TreeEdge[],
  placed: Map<string, PlacedPerson>,
  junctions: UnionJunction[],
  generation: Map<string, number>,
): ChildDrop[] {
  const parentsOf = new Map<string, TreeEdge[]>();
  for (const edge of edges) {
    if (edge.kind !== 'parent_child') continue;
    if (!placed.has(edge.a) || !placed.has(edge.b)) continue;
    parentsOf.set(edge.b, [...(parentsOf.get(edge.b) ?? []), edge]);
  }

  const junctionFor = (x: string, y: string): UnionJunction | undefined =>
    junctions.find(
      (junction) =>
        (junction.a === x && junction.b === y) || (junction.a === y && junction.b === x),
    );

  const drops: ChildDrop[] = [];
  for (const [childId, parentEdges] of parentsOf) {
    let shared: UnionJunction | undefined;
    if (parentEdges.length >= 2) {
      outer: for (let i = 0; i < parentEdges.length; i += 1) {
        for (let j = i + 1; j < parentEdges.length; j += 1) {
          const found = junctionFor(parentEdges[i]!.a, parentEdges[j]!.a);
          if (found) {
            shared = found;
            break outer;
          }
        }
      }
    }
    if (shared && !shared.children.includes(childId)) shared.children.push(childId);

    for (const edge of parentEdges) {
      const inShared = shared != null && (shared.a === edge.a || shared.b === edge.a);
      const parentGeneration = generation.get(edge.a) ?? 0;
      const childGeneration = generation.get(edge.b) ?? 0;
      drops.push({
        id: edge.id,
        parentId: edge.a,
        childId,
        junctionId: inShared ? shared!.id : null,
        basis: edge.parent_basis ?? 'unknown',
        // §4.5: an edge that does not span exactly one band routes through the
        // gutters, so a grandparent's adoption stays parseable mid-viewport.
        crossBand: childGeneration - parentGeneration !== 1,
        status: edge.status,
      });
    }
  }

  // Children under a junction read oldest-first, nulls last — the ordering rule
  // every list in the application shares (docs/02 §2.3).
  for (const junction of junctions) {
    junction.children.sort((x, y) => {
      const kx = fuzzyDateSortKey(placed.get(x)?.person.birth);
      const ky = fuzzyDateSortKey(placed.get(y)?.person.birth);
      if (kx === ky) return x.localeCompare(y);
      if (kx == null) return 1;
      if (ky == null) return -1;
      return kx - ky;
    });
  }

  return drops;
}

/** Groups, exported for the tests that assert rail composition. */
export type { CoupleGroup, PersonSummary };
